/**
 * @file AppContext.jsx
 *
 * App Context
 * =============
 *
 * Global application context for shared state across components.
 * Currently handles:
 * - Theme mode (light/dark) - prepared for future dark mode support
 * - App-wide configuration
 *
 * This context is designed to be extended as the app grows.
 * Add new global state here instead of prop drilling through components.
 *
 * Usage:
 *   // In a component
 *   import { useAppContext } from '@/client/context';
 *   const { isDarkMode, toggleTheme } = useAppContext();
 *
 *   // Wrap app with provider (already done in index.jsx)
 *   <AppProvider>
 *     <App />
 *   </AppProvider>
 */

import { createContext, useContext, useState, useEffect, useMemo } from "react";

/**
 * Default context values
 * @type {Object}
 */
const defaultContextValue = {
    isDarkMode: false,
    appName: "Consultto",
    version: "1.0.0",
    pageColor: null,
    setPageColor: () => {},
};

/**
 * Create the context with default values
 */
const AppContext = createContext(defaultContextValue);

/**
 * Custom hook to use the app context
 * Throws an error if used outside of AppProvider
 * @returns {Object} App context value
 */
export function useAppContext() {
    const context = useContext(AppContext);
    if (context === undefined) {
        throw new Error("useAppContext must be used within an AppProvider");
    }
    return context;
}

/**
 * App Provider Component
 * Wraps the application and provides global state
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components
 * @returns {React.ReactElement}
 */
export function AppProvider({ children }) {
    // Detect Dark Reader: it injects <style class="darkreader"> elements
    const [darkReaderActive] = useState(() => document.querySelectorAll(".darkreader").length > 0);

    // If Dark Reader is active, lock it out so our own dark theme takes over
    useEffect(() => {
        if (!darkReaderActive) return;
        const meta = document.createElement("meta");
        meta.name = "darkreader-lock";
        document.head.appendChild(meta);
        return () => meta.remove();
    }, [darkReaderActive]);

    // Page-level color override (e.g. doctor's profile color on appointment pages)
    const [pageColor, setPageColor] = useState(null);

    // Dark Reader active → always dark; otherwise follow OS/browser preference
    const [isDarkMode, setIsDarkMode] = useState(
        () => darkReaderActive || (window.matchMedia?.("(prefers-color-scheme: dark)").matches || false)
    );

    useEffect(() => {
        if (darkReaderActive) return; // locked to dark, no need to listen
        const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
        if (!mq) return;
        const handler = (e) => setIsDarkMode(e.matches);
        mq.addEventListener("change", handler);
        return () => mq.removeEventListener("change", handler);
    }, [darkReaderActive]);

    // Memoize context value to prevent unnecessary re-renders
    const contextValue = useMemo(
        () => ({
            isDarkMode,
            appName: "Consultto",
            version: "1.0.0",
            pageColor,
            setPageColor,
        }),
        [isDarkMode, pageColor]
    );

    return (
        <AppContext.Provider value={contextValue}>
            {children}
        </AppContext.Provider>
    );
}

export default AppContext;
